import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { cn } from "~/utils/fontsConfig";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/shadcn/Select";
import { toSameTimeDiffTZ } from "~/utils/time";
import { Button } from "./Button";
import { GoXCircle } from "react-icons/go";

const timeOptions = [
    "--:--",
    ...Array.from({ length: 12 * 4 }, (v, i) =>
        [
            `${Math.floor(i / 4) + 1}`.padStart(2, "0"),
            `${(i % 4) * 15}`.padStart(2, "0"),
        ].join(":"),
    ),
];

const TimeInput = ({
    time,
    timeZone,
    formField,
    isEditMode,
    required,
    className,
    minTime,
}: {
    time: Date | null;
    timeZone?: string;
    formField: string;
    isEditMode: boolean;
    required?: boolean;
    className?: string;
    // used to filter event end time values
    // so that we only show end times AFTER the selected
    // event start time
    minTime?: string;
}) => {
    const form = useFormContext();
    const [hour, setHour] = useState<string>();
    const [meridian, setMeridian] = useState<string>();

    const updateTimeField = (hour?: string, meridian?: string) => {
        if (!hour || !meridian) {
            return;
        }

        let [onlyHour, onlyMinutes] = hour.split(":");
        if (!onlyHour || !onlyMinutes) {
            return;
        }

        let hour24;

        if (meridian === "AM") {
            hour24 =
                onlyHour === "12"
                    ? `00:${onlyMinutes}`
                    : `${onlyHour}:${onlyMinutes}`;
        } else {
            // PM
            hour24 =
                onlyHour === "12"
                    ? `${onlyHour}:${onlyMinutes}`
                    : `${parseInt(onlyHour) + 12}:${onlyMinutes}`;
        }

        form.setValue(formField, `${hour24}`, {
            shouldDirty: true,
        });
    };

    useEffect(() => {
        if (time && !hour) {
            const h = Intl.DateTimeFormat("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                timeZone: "UTC",
                hour12: true,
            }).format(time);

            setHour(h.split(" ")[0]);
            setMeridian(h.split(" ")[1]);
        }
    }, [time]);

    const handleHourChange = (e?: string) => {
        if (e === "--:--") {
            form.setValue(formField, undefined, {
                shouldDirty: true,
            });
            form.setValue("isTimeSet", false, {
                shouldDirty: true,
            });
            setMeridian(undefined);
            setHour(undefined);
            return;
        }
        setHour(e);
        updateTimeField(e, meridian);
    };

    const handleMeridianChange = (e?: string) => {
        setMeridian(e);
        updateTimeField(hour, e);
    };

    const handleResetTBD = () => {
        form.setValue(formField, undefined, {
            shouldDirty: true,
        });
        form.setValue("isTimeSet", false, {
            shouldDirty: true,
        });
        setMeridian(undefined);
        setHour(undefined);
    };

    // filter out time options after the minTime
    const filteredTimeOptions = React.useMemo(() => {
        if (!minTime) return timeOptions;

        const [minHourStr, minMinuteStr] = minTime.split(":");
        if (!minHourStr || !minMinuteStr) return timeOptions;

        const minHour = parseInt(minHourStr);
        const minMinute = parseInt(minMinuteStr);
        if (isNaN(minHour) || isNaN(minMinute)) return timeOptions;

        return timeOptions.filter((option) => {
            if (option === "--:--") return true;

            const [h, m] = option.split(":");
            if (!h || !m) return false;

            const hour = parseInt(h);
            const minute = parseInt(m);

            // if no meridian selected yet, check if the time would be valid
            // in either AM or PM
            if (!meridian) {
                // check AM case
                const amHour = hour === 12 ? 0 : hour;
                // check PM case
                const pmHour = hour === 12 ? 12 : hour + 12;

                // show time if it would be valid in either AM or PM
                // and ensure it's strictly after minTime
                return (
                    amHour > minHour ||
                    (amHour === minHour && minute > minMinute) ||
                    pmHour > minHour ||
                    (pmHour === minHour && minute > minMinute)
                );
            }

            // normal case when meridian is selected
            let compareHour = hour;
            if (meridian === "PM" && hour !== 12) compareHour += 12;
            else if (meridian === "AM" && hour === 12) compareHour = 0;

            // Changed from >= to > to make it strictly after
            if (compareHour < minHour) return false;
            if (compareHour === minHour && minute <= minMinute) return false;
            return true;
        });
    }, [minTime, meridian]);

    if (isEditMode) {
        return (
            <div className="relative flex w-full flex-col">
                <div className={cn("flex items-center gap-2", className)}>
                    <Select value={hour} onValueChange={handleHourChange}>
                        <SelectTrigger className="h-auto w-[100px] px-2 py-2">
                            <SelectValue placeholder="--:--" />
                        </SelectTrigger>

                        <SelectContent
                            className="h-[250px] px-0 py-0"
                            side="bottom"
                        >
                            <SelectGroup className="px-0 py-0">
                                {filteredTimeOptions.map((option) => (
                                    <SelectItem key={option} value={option}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>

                    <Select
                        value={meridian}
                        onValueChange={handleMeridianChange}
                    >
                        <SelectTrigger className="h-auto w-[90px]">
                            <SelectValue placeholder="--:--" />
                        </SelectTrigger>

                        <SelectContent side="bottom">
                            <SelectGroup className="px-0 py-0">
                                <SelectItem value="AM">AM</SelectItem>
                                <SelectItem value="PM">PM</SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>

                    <Button
                        onClick={handleResetTBD}
                        className="w-[40px] rounded-md border border-v3-N-500 bg-v3-N-500 text-white"
                    >
                        <GoXCircle className="h-4 w-4" />
                    </Button>
                </div>
                {required && (!hour || !meridian) && (
                    <div className="mt-1 flex min-h-[20px] w-full text-red-500 sm:justify-end">
                        <p>This field is required</p>
                    </div>
                )}
            </div>
        );
    }

    if (!time) return false;

    if (form.watch("isVirtual") && timeZone) {
        // virtual events get time displayed in local browser tz
        const localTime = toSameTimeDiffTZ(time, timeZone, "system").toJSDate();
        return (
            <span>
                {Intl.DateTimeFormat("en-US", {
                    timeZone: "UTC",
                    timeStyle: "short",
                }).format(localTime)}
            </span>
        );
    }

    return (
        <span>
            {hour?.replace(/^0/, "")} {meridian}
        </span>
    );
};
export default TimeInput;
